// sceneState.ts — pure helpers, no React, no R3F. Anything in the
// application that needs to know "which scene are we in?" or "where
// should the camera be at scroll progress p?" imports from here.
//
// All functions are deterministic and side-effect-free. The single
// dependency is Three.js for Vector3, kept out of the call signatures
// (functions take/return [x,y,z] tuples) so this module could be unit
// tested without a renderer.

import { Vector3 } from 'three';
import { SCENES, type SceneConfig, type Vec3 } from './planetSections';

// ---------------------------------------------------------------------------
// Shape: the live scene state. This is the contract every consumer
// (PlanetScene, SceneCameraRig, HUD) agrees on. It is a *value*, not
// a singleton — the React layer / R3F frame loop build it each tick
// and pass it down.
//
// @typedef {object} SceneState
// @property {number} progress              0..1 scroll progress
// @property {number} activeSceneIndex      0..6
// @property {number} blendInScene          0..1 — how far into the active
//                                         scene we are (0 at start, 1 at end)
// @property {THREE.Vector3} cameraPos
// @property {THREE.Vector3} cameraLookAt
// @property {number} planetRotation        radians
// @property {string|null} activeLandmarkKey
// @property {string|null} activePanelKey
// @property {boolean} isDragging
// @property {boolean} isReducedMotion
// @property {boolean} webGLAvailable
// @property {string|null} activeSatelliteId
// @property {string|null} hoveredSatelliteId
// @property {string|null} hoveredNoteId
// @property {string|null} openPanelId
// ---------------------------------------------------------------------------

/** @type {import('./planetSections').SceneConfig} */
export type { SceneConfig };

/**
 * Find the scene that contains scroll progress `p` and how far into it
 * we are. `blendInScene` is 0 at the start of the scene and 1 at the
 * end — useful for cross-fading HUD chips or landmark opacity.
 *
 * @param {number} p — 0..1
 * @returns {{ idx: number, blendIn: number }}
 */
export function sceneAtProgress(p: number): { idx: number; blendIn: number } {
  const clamped = clamp01(p);
  for (let i = 0; i < SCENES.length; i++) {
    const s = SCENES[i];
    if (clamped >= s.start && clamped <= s.end) {
      const span = s.end - s.start;
      const blend = span > 0 ? (clamped - s.start) / span : 0;
      return { idx: i, blendIn: blend };
    }
  }
  // Past the end — clamp to final scene, full blend.
  if (clamped > SCENES[SCENES.length - 1].end) {
    return { idx: SCENES.length - 1, blendIn: 1 };
  }
  return { idx: 0, blendIn: 0 };
}

/**
 * Get the current scene config for a given scroll progress.
 *
 * @param {number} p
 * @returns {SceneConfig}
 */
export function getActiveScene(p: number): SceneConfig {
  const { idx } = sceneAtProgress(p);
  return SCENES[idx];
}

/**
 * Compute the camera state at scroll progress `p` by interpolating
 * between the previous and next scene. We use ease-in-out (smoothstep)
 * on the per-segment progress so the camera glides instead of
 * accelerating at scene boundaries.
 *
 * @param {number} p
 * @returns {{ pos: Vector3, lookAt: Vector3 }}
 */
export function getCameraState(p: number): { pos: Vector3; lookAt: Vector3 } {
  const clamped = clamp01(p);
  // Find the segment we're inside.
  for (let i = 0; i < SCENES.length - 1; i++) {
    const a = SCENES[i];
    const b = SCENES[i + 1];
    if (clamped >= a.start && clamped <= b.end) {
      // Map clamped into [a.start, b.end] then into [0,1] within segment.
      const segmentLen = b.end - a.start;
      const localT = segmentLen > 0 ? (clamped - a.start) / segmentLen : 0;
      const t = smoothstep(clamp01(localT));
      return lerpCameraStates(a, b, t);
    }
  }
  // Past the end — return final scene pose.
  const last = SCENES[SCENES.length - 1];
  return {
    pos: vec3FromTuple(last.cameraPos),
    lookAt: vec3FromTuple(last.cameraLookAt),
  };
}

/**
 * Planet rotation at scroll progress `p` — interpolated between scene
 * anchors. Wrapping math: a full revolution is 2π, so we lerp in
 * angle-space but on the shorter arc to avoid the camera "spinning
 * the long way" between adjacent scenes.
 *
 * @param {number} p
 * @returns {number} rotation in radians
 */
export function getPlanetRotation(p: number): number {
  const clamped = clamp01(p);
  for (let i = 0; i < SCENES.length - 1; i++) {
    const a = SCENES[i];
    const b = SCENES[i + 1];
    if (clamped >= a.start && clamped <= b.end) {
      const segmentLen = b.end - a.start;
      const t = segmentLen > 0 ? smoothstep((clamped - a.start) / segmentLen) : 0;
      return lerpAngle(a.planetRotation, b.planetRotation, t);
    }
  }
  return SCENES[SCENES.length - 1].planetRotation;
}

/**
 * Lerp helper for two scene camera states. Position and lookAt are
 * component-wise Vector3 lerps; rotation is NOT part of the camera
 * state here (use getPlanetRotation for that).
 *
 * @param {SceneConfig} a
 * @param {SceneConfig} b
 * @param {number} t — 0..1
 * @returns {{ pos: Vector3, lookAt: Vector3 }}
 */
export function lerpCameraStates(
  a: SceneConfig,
  b: SceneConfig,
  t: number,
): { pos: Vector3; lookAt: Vector3 } {
  const tt = clamp01(t);
  const posA = vec3FromTuple(a.cameraPos);
  const posB = vec3FromTuple(b.cameraPos);
  const lookA = vec3FromTuple(a.cameraLookAt);
  const lookB = vec3FromTuple(b.cameraLookAt);
  return {
    pos: posA.lerp(posB, tt),
    lookAt: lookA.lerp(lookB, tt),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Standard smoothstep — gives a "ease in, ease out" feel. */
function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

/** Lerp between two angles, taking the shorter arc. */
function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  // Normalize diff into [-π, π]
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * clamp01(t);
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function vec3FromTuple(t: Vec3): Vector3 {
  return new Vector3(t[0], t[1], t[2]);
}
